import { Anchor, Button, Flex, Grid, Stack, Title } from '@mantine/core';
import { useEffect, useState } from 'react';

import { Declaration, EnvironmentCountries } from '@e-origin/shared';

import { EucdmCode, HideElementFor, LeavePageNotification, MultiLangCodes, NumberInput, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { disableAmendmentField, isHighValueDeclaration } from '@utils';
import { useDeclarationViewCustomsTreatmentForm } from './useDeclarationViewCustomsTreatmentForm';

interface DeclarationViewCustomsTreatmentProps {
  editDisabled: boolean;
  declaration: Declaration;
}

export const DeclarationViewCustomsTreatment: React.FC<DeclarationViewCustomsTreatmentProps> = ({ editDisabled, declaration }) => {
  const { form, isExport, onSubmit, isSaving, buildFormValues } = useDeclarationViewCustomsTreatmentForm({ declaration });
  const [declarationGroup, setDeclarationGroup] = useState<string>(declaration?.generalInfo?.group);

  const { data: settings } = useSettings();

  const isNetherlands = settings.country === EnvironmentCountries.NL;
  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport,
  });

  useEffect(() => {
    form.setValues(buildFormValues(declaration));
    form.resetDirty({});
    setDeclarationGroup(declaration?.generalInfo?.group);
  }, [declaration]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <form onSubmit={form.onSubmit(onSubmit)} style={{ paddingRight: '30%' }}>
        <Stack mb="xl">
          <Title order={5} fw={600}>
            Customs treatment
          </Title>

          {!settings.isForwarder && !isNetherlands && (
            <>
              <Grid>
                <Grid.Col span={4}>
                  <EucdmCode path="messageInfo.methodOfPayment">
                    <MultiLangCodes
                      {...form.getInputProps('messageInfo.methodOfPayment')}
                      label="Payment Method"
                      code="CL104"
                      disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
                      system={codelistSystems}
                    />
                  </EucdmCode>
                </Grid.Col>
                <Grid.Col span={4}>
                  <EucdmCode path="messageInfo.deferredPayment">
                    <TextInput
                      {...form.getInputProps('messageInfo.deferredPayment')}
                      label="Deferred payment"
                      disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
                    />
                  </EucdmCode>
                </Grid.Col>
                <HideElementFor systems={['DMS', 'DECO', 'IDMS_LV']} declarationGroup={declarationGroup}>
                  <Grid.Col span={4}>
                    <EucdmCode path="documents.additionalFiscalReference.accountHolder">
                      <TextInput
                        label="Account Holder"
                        value={declaration?.documents?.additionalFiscalReference?.find((ref) => ref.role === 'FR4')?.vatIdentificationNumber}
                        disabled
                      />
                    </EucdmCode>
                  </Grid.Col>
                </HideElementFor>
              </Grid>
              <Grid>
                <Grid.Col span={6}>
                  <EucdmCode path="messageInfo.methodOfPayment">
                    <MultiLangCodes
                      {...form.getInputProps('messageInfo.methodOfPaymentVAT')}
                      label="VAT Payment Method"
                      code="CL104"
                      disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
                      system={codelistSystems}
                    />
                  </EucdmCode>
                </Grid.Col>
                <Grid.Col span={6}>
                  <EucdmCode path="messageInfo.deferredPaymentVAT">
                    <TextInput
                      {...form.getInputProps('messageInfo.deferredPaymentVAT')}
                      label="Deferred payment VAT"
                      disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
                    />
                  </EucdmCode>
                </Grid.Col>
              </Grid>
            </>
          )}
          <HideElementFor systems={['DECO', 'IDMS_LV']} declarationGroup={declarationGroup}>
            <Grid>
              <Grid.Col span={5}>
                <EucdmCode path="valuationInformationTaxes.valuationMethod">
                  <MultiLangCodes
                    {...form.getInputProps('valuationInformationTaxes.valuationMethod')}
                    label="Valuation method"
                    code="CL604"
                    disabled={editDisabled}
                    system={codelistSystems}
                  />
                </EucdmCode>
              </Grid.Col>
              {!settings?.isForwarder && (
                <Grid.Col span={5}>
                  <EucdmCode path="valuationInformationTaxes.valuationAdjustment.valuationIndicators">
                    <TextInput
                      {...form.getInputProps('valuationInformationTaxes.valuationAdjustment.valuationIndicators')}
                      label="Valuation indicators"
                      disabled={editDisabled}
                    />
                  </EucdmCode>
                </Grid.Col>
              )}
              {isNetherlands && (
                <Grid.Col span={2}>
                  <Flex align="flex-end">
                    <Anchor href={'/help'}>Valuation indicators description</Anchor>
                  </Flex>
                </Grid.Col>
              )}
            </Grid>
          </HideElementFor>
        </Stack>

        {!isExport && (
          <Stack mb="xl">
            <Title order={5} fw={600}>
              Global charges import
            </Title>

            <Grid>
              <Grid.Col span={6}>
                <EucdmCode path="valuationInformationTaxes.globalChargesimport.CustomsCharges.charges">
                  <NumberInput
                    {...form.getInputProps('valuationInformationTaxes.globalChargesimport.CustomsCharges.charges')}
                    label="Customs charges"
                    disabled={editDisabled}
                  />
                </EucdmCode>
              </Grid.Col>
              <Grid.Col span={6}>
                <MultiLangCodes
                  {...form.getInputProps('valuationInformationTaxes.globalChargesimport.CustomsCharges.currency')}
                  label="Currency"
                  code="Currency"
                  disabled={editDisabled}
                  system={codelistSystems}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <EucdmCode path="valuationInformationTaxes.globalChargesimport.VATcharges.charges">
                  <NumberInput
                    {...form.getInputProps('valuationInformationTaxes.globalChargesimport.VATcharges.charges')}
                    label="VAT charges"
                    disabled={editDisabled}
                  />
                </EucdmCode>
              </Grid.Col>
              <Grid.Col span={6}>
                <MultiLangCodes
                  {...form.getInputProps('valuationInformationTaxes.globalChargesimport.VATcharges.currency')}
                  label="Currency"
                  code="Currency"
                  disabled={editDisabled}
                  system={codelistSystems}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        )}

        {!isExport && isHighValueDeclaration(declaration.generalInfo.group) ? (
          <Stack mb="xl">
            <Title order={5} fw={600}>
              Charges import
            </Title>

            <Grid>
              <Grid.Col span={6}>
                <EucdmCode path="valuationInformationTaxes.globalChargesimport.CustomsCharges.charges">
                  <NumberInput
                    {...form.getInputProps('valuationInformationTaxes.chargesimport.CustomsCharges.charges')}
                    label="Customs charges"
                    disabled={editDisabled}
                  />
                </EucdmCode>
              </Grid.Col>
              <Grid.Col span={6}>
                <MultiLangCodes
                  {...form.getInputProps('valuationInformationTaxes.chargesimport.CustomsCharges.currency')}
                  label="Currency"
                  code="Currency"
                  disabled={editDisabled}
                  system={codelistSystems}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <EucdmCode path="valuationInformationTaxes.globalChargesimport.VATcharges.charges">
                  <NumberInput
                    {...form.getInputProps('valuationInformationTaxes.chargesimport.VATcharges.charges')}
                    label="VAT charges"
                    disabled={editDisabled}
                  />
                </EucdmCode>
              </Grid.Col>
              <Grid.Col span={6}>
                <MultiLangCodes
                  {...form.getInputProps('valuationInformationTaxes.chargesimport.VATcharges.currency')}
                  label="Currency"
                  code="Currency"
                  disabled={editDisabled}
                  system={codelistSystems}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        ) : (
          <Stack mb="xl">
            <Title order={5}>Transport and insurance costs</Title>
            <Grid>
              <Grid.Col span={6}>
                <EucdmCode path="goodsShipmentItems.transportInsurance.currencyCode">
                  <MultiLangCodes
                    {...form.getInputProps('consignment.transportCost.currencyCode')}
                    label="Currency code"
                    code="CL048"
                    disabled={editDisabled}
                    system={codelistSystems}
                  />
                </EucdmCode>
              </Grid.Col>
              <Grid.Col span={6}>
                <EucdmCode path="goodsShipmentItems.transportInsurance.amount">
                  <NumberInput {...form.getInputProps('consignment.transportCost.amount')} label="Amount" disabled={editDisabled} />
                </EucdmCode>
              </Grid.Col>
            </Grid>
          </Stack>
        )}
        {!editDisabled && (
          <Button
            data-pw="submit-btn"
            type="submit"
            key="save-modal-btn"
            disabled={editDisabled || !form.isValid()}
            loading={isSaving}
            variant="filled"
            size="lg"
            my="xl"
          >
            Save
          </Button>
        )}
      </form>
    </>
  );
};
